import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import { type AgencyPostConfirmation, PostToAgencyDialog } from "@/components/staffing/PostToAgencyDialog";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useAgencyAvailability } from "@/hooks/useAgencyAvailability";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import type { AgencySource, ShiftRequest } from "@/utils/models";
import { useCanPostRequestToAgency } from "./useCanPostRequestToAgency";
import { usePostRequestToAgency } from "./usePostRequestToAgency";

export interface PostToAgencyControlState {
	/** Open shift, still unfilled, not posted, agency configured, and job/role maps to a sub-role. */
	canNewlyPostToAgency: boolean;
	/** The request already carries an agency_source_name. */
	isPostedToAgency: boolean;
	/** The department's agencies with a facility configured (`useCanPostRequestToAgency`'s); empty when it has none. */
	/** Agencies that are configured AND switched on (DAT-885). Was `configuredAgencies`, which
	 * stopped being true once an integration could be deactivated while keeping its facility id. */
	availableAgencies: AgencySource[];
	/** A post mutation is in flight. */
	isPosting: boolean;
	/** Open the confirm dialog. */
	openDialog: () => void;
	/** The confirm dialog, ready to render (null while closed). Render once per control instance. */
	dialog: ReactNode;
}

/**
 * Shared behavior for every "post to agency" surface (Requests tab, daily-gaps posted shifts + gap cells).
 * Owns the confirm-dialog state and the post mutation, and re-exposes the eligibility booleans, so each
 * presentation (button / chip / icon-toggle) only decides how to render — not what the rules are.
 */
export function usePostToAgencyControl(request: ShiftRequest): PostToAgencyControlState {
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { roleGroups, isPostedToAgency, canNewlyPostToAgency } = useCanPostRequestToAgency(request);
	const { availableAgencies } = useAgencyAvailability();
	const { mutateAsync: postRequestToAgency, isPending: isPosting } = usePostRequestToAgency();
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const openDialog = useCallback(() => setIsDialogOpen(true), []);
	const closeDialog = useCallback(() => setIsDialogOpen(false), []);

	const handleConfirm = useCallback(
		async ({ agencySource, subRole, postWithDelay, agencyPostDelayHours }: AgencyPostConfirmation) => {
			await postRequestToAgency({ requestId: request.id, agencySource, subRole, postWithDelay, agencyPostDelayHours });
			setIsDialogOpen(false);
		},
		[postRequestToAgency, request.id],
	);

	const dialog = isDialogOpen ? (
		<PostToAgencyDialog
			open={isDialogOpen}
			jobId={request.job_id}
			roleGroups={roleGroups}
			jobManagementEnabled={jobManagementEnabled}
			onClose={closeDialog}
			onConfirm={handleConfirm}
			confirmLoading={isPosting}
		/>
	) : null;

	return { canNewlyPostToAgency, isPostedToAgency, availableAgencies, isPosting, openDialog, dialog };
}
